import React, { useState } from 'react';
import { MainLayout } from '../components/Layout';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  HelpCircle,
  Search,
  LayoutDashboard,
  FileSpreadsheet,
  Calculator,
  ClipboardCheck,
  ClipboardList,
  Kanban,
  Link2,
  Sparkles,
  BarChart3,
  Settings,
  ChevronDown,
  ChevronRight,
  Play,
  CheckCircle2,
  AlertCircle,
  Info,
  BookOpen,
  Users,
  Shield,
  Workflow,
  ArrowRight,
  ArrowDown,
  RefreshCw,
  Edit,
  Eye,
  Plus,
  Filter,
  Download,
  Mail,
  MessageSquare
} from 'lucide-react';

const HelpSection = ({ title, icon: Icon, children, defaultOpen = false, isDark }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={`border ${isDark ? 'border-zinc-800' : 'border-zinc-200'} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 flex items-center justify-between ${isDark ? 'bg-zinc-900 hover:bg-zinc-800/50' : 'bg-white hover:bg-zinc-50'} transition-colors`}
      >
        <div className="flex items-center gap-3">
          <div className={`p-2 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded-lg`}>
            <Icon className="w-5 h-5 text-[#C3002F]" />
          </div>
          <span className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`} />
        ) : (
          <ChevronRight className={`w-5 h-5 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`} />
        )}
      </button>
      {isOpen && (
        <div className={`p-6 ${isDark ? 'bg-zinc-950 border-zinc-800' : 'bg-zinc-50 border-zinc-200'} border-t`}>
          {children}
        </div>
      )}
    </div>
  );
};

const WorkflowStep = ({ number, title, description, icon: Icon, isDark }) => (
  <div className="flex items-start gap-4">
    <div className="flex-shrink-0 w-10 h-10 bg-[#C3002F] rounded-full flex items-center justify-center text-white font-bold">
      {number}
    </div>
    <div className="flex-1">
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className={`w-4 h-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`} />}
        <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>{title}</h4>
      </div>
      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{description}</p>
    </div>
  </div>
);

const FeatureCard = ({ title, description, icon: Icon, color = "emerald", isDark }) => {
  const colors = {
    emerald: "bg-emerald-500/10 border-emerald-500/30 text-emerald-400",
    blue: "bg-blue-500/10 border-blue-500/30 text-blue-400",
    purple: "bg-purple-500/10 border-purple-500/30 text-purple-400",
    amber: "bg-amber-500/10 border-amber-500/30 text-amber-400",
    red: "bg-red-500/10 border-red-500/30 text-red-400",
  };

  return (
    <div className={`p-4 rounded-lg border ${colors[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-5 h-5" />
        <h4 className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>{title}</h4>
      </div>
      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{description}</p>
    </div>
  );
};

const StatusBadge = ({ status, description, isDark }) => {
  const styles = {
    draft: isDark ? 'bg-zinc-800 text-zinc-400' : 'bg-zinc-200 text-zinc-600',
    pending: 'bg-amber-500/20 text-amber-400',
    in_review: 'bg-blue-500/20 text-blue-400',
    approved: 'bg-emerald-500/20 text-emerald-400',
    rejected: 'bg-red-500/20 text-red-400',
    on_hold: 'bg-orange-500/20 text-orange-400',
    needs_info: 'bg-purple-500/20 text-purple-400'
  };

  return (
    <div className={`flex items-center gap-3 p-3 ${isDark ? 'bg-zinc-900' : 'bg-zinc-100'} rounded-lg`}>
      <span className={`px-3 py-1 text-xs rounded-full ${styles[status]}`}>
        {status.replace('_', ' ')}
      </span>
      <span className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>{description}</span>
    </div>
  );
};

export default function HelpSupportPage() {
  const { isDark } = useTheme();
  const [searchQuery, setSearchQuery] = useState('');

  return (
    <MainLayout>
      <div className="p-8 space-y-8" data-testid="help-support-page">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto">
          <div className="inline-flex p-4 bg-[#C3002F]/10 rounded-2xl mb-4">
            <HelpCircle className="w-12 h-12 text-[#C3002F]" />
          </div>
          <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-zinc-900'} mb-4`}>Help & Support Center</h1>
          <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} text-lg`}>
            Welcome to the COGS Management Portal help center. Find comprehensive guides,
            workflows, and feature explanations to help you get started.
          </p>
        </div>

        {/* Search */}
        <div className="max-w-2xl mx-auto">
          <div className="relative">
            <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 ${isDark ? 'text-zinc-500' : 'text-zinc-400'}`} />
            <Input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for help topics..."
              className={`pl-12 py-6 text-lg ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}
            />
          </div>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <a href="#getting-started" className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-xl hover:border-[#C3002F]/50 transition-colors text-center`}>
            <Play className="w-6 h-6 text-[#C3002F] mx-auto mb-2" />
            <span className={`text-sm ${isDark ? 'text-white' : 'text-zinc-900'}`}>Getting Started</span>
          </a>
          <a href="#workflows" className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-xl hover:border-[#C3002F]/50 transition-colors text-center`}>
            <Workflow className="w-6 h-6 text-[#C3002F] mx-auto mb-2" />
            <span className={`text-sm ${isDark ? 'text-white' : 'text-zinc-900'}`}>Workflows</span>
          </a>
          <a href="#features" className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-xl hover:border-[#C3002F]/50 transition-colors text-center`}>
            <BookOpen className="w-6 h-6 text-[#C3002F] mx-auto mb-2" />
            <span className={`text-sm ${isDark ? 'text-white' : 'text-zinc-900'}`}>Features Guide</span>
          </a>
          <a href="#roles" className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-xl hover:border-[#C3002F]/50 transition-colors text-center`}>
            <Users className="w-6 h-6 text-[#C3002F] mx-auto mb-2" />
            <span className={`text-sm ${isDark ? 'text-white' : 'text-zinc-900'}`}>User Roles</span>
          </a>
        </div>

        {/* Help Sections */}
        <div className="space-y-4 max-w-5xl mx-auto">

          {/* Getting Started */}
          <div id="getting-started">
            <HelpSection title="Getting Started" icon={Play} defaultOpen={true} isDark={isDark}>
              <div className="space-y-6">
                <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                  The COGS (Cost of Goods Sold) Management Portal helps you manage pricing for automotive parts.
                  Here's how to get started:
                </p>

                <div className="grid md:grid-cols-3 gap-4">
                  <FeatureCard
                    icon={Users}
                    title="1. Login"
                    description="Use your credentials to access the portal. Contact admin if you need access."
                    color="blue"
                    isDark={isDark}
                  />
                  <FeatureCard
                    icon={LayoutDashboard}
                    title="2. Dashboard"
                    description="View key metrics, pending tasks, and quick actions from the dashboard."
                    color="emerald"
                    isDark={isDark}
                  />
                  <FeatureCard
                    icon={ClipboardCheck}
                    title="3. Create Cards"
                    description="Search for parts and create COGS cards with calculated pricing."
                    color="purple"
                    isDark={isDark}
                  />
                </div>

                <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="w-5 h-5 text-blue-400 mt-0.5" />
                    <div>
                      <p className="font-medium text-blue-400">Pro Tip</p>
                      <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                        Use the sidebar navigation to access all features. The sidebar shows different options based on your role.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </HelpSection>
          </div>

          {/* User Roles */}
          <div id="roles">
            <HelpSection title="User Roles & Permissions" icon={Shield} isDark={isDark}>
              <div className="space-y-6">
                <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                  The system has different user roles with specific permissions:
                </p>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 text-xs bg-[#C3002F]/20 text-[#C3002F] rounded">Admin</span>
                    </div>
                    <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Full system access</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Manage users and roles</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Create and edit templates</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Approve/reject any card</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Access analytics and insights</li>
                    </ul>
                  </div>

                  <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 text-xs bg-cyan-500/20 text-cyan-400 rounded">Approver</span>
                    </div>
                    <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Review submitted cards</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Approve or reject cards</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Request more information</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Edit values before approval</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> View analytics</li>
                    </ul>
                  </div>

                  <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className="px-2 py-1 text-xs bg-amber-500/20 text-amber-400 rounded">Creator</span>
                    </div>
                    <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Search parts</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Create new COGS cards</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Edit own cards</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Submit for approval</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Revise rejected cards</li>
                    </ul>
                  </div>

                  <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`px-2 py-1 text-xs ${isDark ? 'bg-zinc-700 text-zinc-400' : 'bg-zinc-200 text-zinc-600'} rounded`}>Viewer</span>
                    </div>
                    <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> View approved cards</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> Search parts</li>
                      <li className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400" /> View analytics (limited)</li>
                      <li className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-400" /> Cannot create or edit</li>
                      <li className="flex items-center gap-2"><AlertCircle className="w-4 h-4 text-red-400" /> Cannot approve</li>
                    </ul>
                  </div>
                </div>
              </div>
            </HelpSection>
          </div>

          {/* Main Workflow */}
          <div id="workflows">
            <HelpSection title="COGS Card Workflow" icon={Workflow} isDark={isDark}>
              <div className="space-y-8">
                <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                  The complete workflow for creating and approving a COGS card:
                </p>

                {/* Visual Workflow */}
                <div className="relative">
                  <div className={`absolute left-5 top-10 bottom-10 w-0.5 ${isDark ? 'bg-zinc-800' : 'bg-zinc-200'}`} />
                  <div className="space-y-8">
                    <WorkflowStep
                      number={1}
                      title="Search for Part"
                      icon={Search}
                      description="Go to 'Part Search' and enter the part number or name. View part details including SAP purchase price, supplier, and family."
                      isDark={isDark}
                    />
                    <WorkflowStep
                      number={2}
                      title="Create COGS Card"
                      icon={Plus}
                      description="Click 'Create COGS Card' button. Select a cost template and currency. The system will automatically calculate pricing."
                      isDark={isDark}
                    />
                    <WorkflowStep
                      number={3}
                      title="Review Calculations"
                      icon={Calculator}
                      description="Review the calculated values: COGS, DBP (Dealer Base Price), MRP, CBP, and margins. Adjust if needed."
                      isDark={isDark}
                    />
                    <WorkflowStep
                      number={4}
                      title="Submit for Approval"
                      icon={ArrowRight}
                      description="Submit the card for approval. It will be assigned to an approver based on the part family or your team settings."
                      isDark={isDark}
                    />
                    <WorkflowStep
                      number={5}
                      title="Approver Review"
                      icon={Eye}
                      description="Approver reviews the card, can edit values, request changes, put on hold, or approve/reject."
                      isDark={isDark}
                    />
                    <WorkflowStep
                      number={6}
                      title="Final Status"
                      icon={CheckCircle2}
                      description="Once approved, the card becomes the official pricing for the part. Rejected cards can be revised and resubmitted."
                      isDark={isDark}
                    />
                  </div>
                </div>

                {/* Status Explanations */}
                <div className="mt-8">
                  <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'} mb-4`}>Card Status Types</h4>
                  <div className="grid md:grid-cols-2 gap-3">
                    <StatusBadge status="draft" description="Card is being created, not yet submitted" isDark={isDark} />
                    <StatusBadge status="pending" description="Submitted and waiting for approver review" isDark={isDark} />
                    <StatusBadge status="in_review" description="Approver is actively reviewing the card" isDark={isDark} />
                    <StatusBadge status="approved" description="Card approved - official pricing" isDark={isDark} />
                    <StatusBadge status="rejected" description="Card rejected - needs revision" isDark={isDark} />
                    <StatusBadge status="on_hold" description="Temporarily paused for additional review" isDark={isDark} />
                    <StatusBadge status="needs_info" description="Approver requested more information" isDark={isDark} />
                  </div>
                </div>
              </div>
            </HelpSection>
          </div>

          {/* Features Guide */}
          <div id="features">
            <HelpSection title="Features Guide" icon={BookOpen} isDark={isDark}>
              <div className="space-y-6">

                {/* Dashboard */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <LayoutDashboard className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Dashboard</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Your central hub showing key metrics and quick actions.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Metrics Cards:</strong> Total parts, pending approvals, approved cards, rejection rate</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Quick Actions:</strong> Create new card, view pending tasks, access reports</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Recent Activity:</strong> Latest card submissions and approvals</li>
                  </ul>
                </div>

                {/* Parts Search */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Search className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Part Search</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Search for parts by part number or name to view details and create COGS cards.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Search:</strong> Enter part number (e.g., NSN-ELC-005) or name (e.g., "Brake Pad")</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Part Details:</strong> SAP price, supplier, lead time, family, description</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Actions:</strong> Create COGS Card or View existing card</li>
                  </ul>
                </div>

                {/* Cost Templates */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <FileSpreadsheet className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Cost Templates</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Pre-configured pricing templates with standard percentages.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>ISC %:</strong> Integrated Selling Cost (logistics, overhead, packaging)</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>GP %:</strong> Gross Profit margin percentage</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>DDR %:</strong> Dealer Discount Rate</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Customs %:</strong> Customs duty for imported parts</li>
                  </ul>
                </div>

                {/* Pricing Models */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Calculator className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Pricing Models</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Different pricing methodologies for various markets.</p>
                  <div className="grid md:grid-cols-2 gap-3 mt-3">
                    <div className={`p-3 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded`}>
                      <p className="font-medium text-emerald-400">Cost Plus Pricing</p>
                      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>Selling Price = Total Cost / (1 - Margin %)</p>
                    </div>
                    <div className={`p-3 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded`}>
                      <p className="font-medium text-blue-400">FOB Pricing</p>
                      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>Export pricing with currency conversion</p>
                    </div>
                    <div className={`p-3 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded`}>
                      <p className="font-medium text-purple-400">ITP (Transfer Pricing)</p>
                      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>Intercompany pricing between entities</p>
                    </div>
                    <div className={`p-3 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded`}>
                      <p className="font-medium text-amber-400">GLP (Global List Price)</p>
                      <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-600'}`}>HQ-dictated pricing with local conversion</p>
                    </div>
                  </div>
                </div>

                {/* COGS Cards */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <ClipboardCheck className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>COGS Cards</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Main workspace to view and manage all pricing cards.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Filters:</strong> By status (pending, approved, etc.), ownership, search</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Card View:</strong> Part details, pricing breakdown, status, history</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Edit Mode:</strong> Modify values and recalculate pricing</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Activity Tab:</strong> View all changes and snapshots</li>
                  </ul>
                </div>

                {/* AI Insights */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <Sparkles className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>AI Insights</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>AI-powered pricing recommendations and optimization opportunities.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Super Seeded Alerts:</strong> Price comparison with replacement parts</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Margin Optimization:</strong> Parts with high margin potential</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>4-Column View:</strong> Current Price → Reference → Recommended → Potential Gain</li>
                  </ul>
                </div>

                {/* Analytics */}
                <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                  <div className="flex items-center gap-3 mb-3">
                    <BarChart3 className="w-6 h-6 text-[#C3002F]" />
                    <h4 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-zinc-900'}`}>Analytics</h4>
                  </div>
                  <p className={`${isDark ? 'text-zinc-400' : 'text-zinc-600'} mb-3`}>Visual reports and metrics for pricing analysis.</p>
                  <ul className={`space-y-2 text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Summary Stats:</strong> Total cards, approval rates, average margins</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Family Analysis:</strong> Pricing breakdown by part family</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Top 10 Parts:</strong> Highest value parts with GP Amount and MRP</li>
                    <li>• <strong className={isDark ? 'text-white' : 'text-zinc-900'}>Trend Charts:</strong> Approval trends over time</li>
                  </ul>
                </div>

              </div>
            </HelpSection>
          </div>

          {/* Pricing Formula */}
          <HelpSection title="Pricing Formula Explained" icon={Calculator} isDark={isDark}>
            <div className="space-y-6">
              <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                The standard pricing calculation follows this formula chain:
              </p>

              <div className={`p-4 ${isDark ? 'bg-zinc-800' : 'bg-zinc-100'} rounded-lg font-mono text-sm space-y-2`}>
                <p className="text-emerald-400">1. ISC Amount = Purchase Price × ISC%</p>
                <p className="text-emerald-400">2. COGS = Purchase Price + Customs Duty + ISC Amount</p>
                <p className="text-cyan-400">3. DBP (Dealer Base Price) = COGS / (1 - GP%)</p>
                <p className="text-cyan-400">4. GP Amount = DBP - COGS</p>
                <p className="text-amber-400">5. MRP = DBP / (1 - DDR%)</p>
                <p className="text-amber-400">6. CBP = MRP / 1.18 (excluding 18% GST)</p>
                <p className="text-purple-400">7. Dealer Margin = CBP - DBP</p>
                <p className="text-purple-400">8. Dealer Margin % = (Dealer Margin / MRP) × 100</p>
              </div>

              <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-400">Important Note</p>
                    <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                      GP% and DDR% are applied on the selling price (not markup on cost).
                      This ensures the margin is a percentage of the final price.
                    </p>
                  </div>
                </div>
              </div>

              {/* Example Calculation */}
              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-zinc-900'} mb-3`}>Example Calculation</h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'} uppercase mb-2`}>Inputs</p>
                    <div className="space-y-1 text-sm">
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>Purchase Price: <span className={isDark ? 'text-white' : 'text-zinc-900'}>₹5,000</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>ISC%: <span className={isDark ? 'text-white' : 'text-zinc-900'}>12.5%</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>GP%: <span className={isDark ? 'text-white' : 'text-zinc-900'}>50%</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>DDR%: <span className={isDark ? 'text-white' : 'text-zinc-900'}>35%</span></p>
                    </div>
                  </div>
                  <div>
                    <p className={`text-xs ${isDark ? 'text-zinc-500' : 'text-zinc-500'} uppercase mb-2`}>Results</p>
                    <div className="space-y-1 text-sm">
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>ISC Amount: <span className="text-emerald-400">₹625</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>COGS: <span className="text-emerald-400">₹5,625</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>DBP: <span className="text-cyan-400">₹11,250</span></p>
                      <p className={isDark ? 'text-zinc-400' : 'text-zinc-600'}>MRP: <span className="text-amber-400">₹17,307.69</span></p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </HelpSection>

          {/* FAQ */}
          <HelpSection title="Frequently Asked Questions" icon={MessageSquare} isDark={isDark}>
            <div className="space-y-4">
              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'} mb-2`}>Q: How do I create a COGS card for a new part?</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  A: Go to "Part Search", enter the part number, click "Search", then click "Create COGS Card" on the result.
                  Select a template, review the calculations, and submit for approval.
                </p>
              </div>

              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'} mb-2`}>Q: What if MRP shows as 0 after editing?</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  A: Click the "Recalculate" button after making changes. This will recalculate all pricing fields
                  based on the updated values.
                </p>
              </div>

              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'} mb-2`}>Q: How do I revise a rejected card?</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  A: Open the rejected card, click "Edit", make the necessary changes based on the rejection comments,
                  click "Recalculate", then "Save & Resubmit". This creates a new version for approval.
                </p>
              </div>

              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'} mb-2`}>Q: What is a Super Seeded part?</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  A: A super seeded part is a newer part that replaces an older one. The system tracks these
                  relationships to help ensure pricing consistency between original and replacement parts.
                </p>
              </div>

              <div className={`p-4 ${isDark ? 'bg-zinc-900' : 'bg-white border border-zinc-200'} rounded-lg`}>
                <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'} mb-2`}>Q: How do I view the history of changes on a card?</p>
                <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>
                  A: Open any COGS card and click on the "Activity" or "History" tab. Activity shows detailed
                  snapshots of each change, while History shows a timeline of status changes.
                </p>
              </div>
            </div>
          </HelpSection>

          {/* Contact Support */}
          <HelpSection title="Contact Support" icon={Mail} isDark={isDark}>
            <div className="space-y-4">
              <p className={isDark ? 'text-zinc-300' : 'text-zinc-700'}>
                Need additional help? Contact the system administrator or support team.
              </p>

              <div className="grid md:grid-cols-2 gap-4">
                <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                  <Mail className="w-6 h-6 text-[#C3002F] mb-2" />
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>Email Support</p>
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>cogs-support@company.com</p>
                </div>
                <div className={`p-4 ${isDark ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'} border rounded-lg`}>
                  <Users className="w-6 h-6 text-[#C3002F] mb-2" />
                  <p className={`font-medium ${isDark ? 'text-white' : 'text-zinc-900'}`}>Admin Contact</p>
                  <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-600'}`}>Contact your system administrator</p>
                </div>
              </div>
            </div>
          </HelpSection>

        </div>
      </div>
    </MainLayout>
  );
}
